import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { PortalMetadata, ThemeProvider } from '@command-center/ui-system';
import { getBranding } from './platform/branding';
import { AuthProvider } from './auth/AuthProvider';
import { ClientPageContextProvider } from './platform/page-context';
import { OrganizationProvider } from './organization/OrganizationProvider';
import { ClientShell } from './components/shell/ClientShell';
import { queryClient } from './lib/queryClient';
import './styles.css';
import './index.css';

class RootErrorBoundary extends Component<{ children: ReactNode }, { error?: Error }> {
  override state: { error?: Error } = {};

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('client-oneup render failed', error, info);
  }

  override render() {
    if (this.state.error) {
      return (
        <main className="fatal-error" role="alert">
          <h1>Client portal failed to render</h1>
          <p>{this.state.error.message}</p>
        </main>
      );
    }

    return this.props.children;
  }
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('client-oneup root element #root was not found in index.html.');
}

const branding = getBranding();

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <RootErrorBoundary>
      <ThemeProvider defaultTheme="light" storageKey="oneup-client-theme" colors={branding.colors}>
        <PortalMetadata branding={branding} />
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <OrganizationProvider>
                <ClientPageContextProvider>
                  <ClientShell />
                </ClientPageContextProvider>
              </OrganizationProvider>
            </AuthProvider>
          </QueryClientProvider>
        </BrowserRouter>
      </ThemeProvider>
    </RootErrorBoundary>
  </React.StrictMode>,
);
